import { FloatingIndicator, Menu, Tabs } from "@mantine/core";
import { useElementSize, useMergedRef } from "@mantine/hooks";
import { ChevronDown, Ellipsis } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { Soon, type SoonProps } from "./Soon";
import classes from "./TypeTabs.module.css";

export interface TypeTab {
  value: string;
  label: string;
  /** A 16px icon on the left. */
  icon?: ReactNode;
  /** A tab of a screen not built yet: shown and focusable, never chosen, "Coming soon". */
  disabled?: boolean;
  /**
   * Faded, for a Coming-soon tab live itself shows disabled ("Ad copy"). Without it a Coming-soon
   * tab keeps live's look (design-system decision 64).
   */
  faded?: boolean;
}

export interface TypeTabsProps {
  /** Names the tab list for a screen reader. */
  label: string;
  tabs: TypeTab[];
  value: string;
  onChange: (value: string) => void;
  /**
   * One line whatever the width, as live's category pills (Templates): the tabs that do not fit
   * fold into a trailing "More" tab, whose menu opens on hover or a click. The first tab always
   * shows. The row takes the width its parent leaves it.
   */
  fold?: boolean;
  /**
   * A row inside a toolbar, one line as a unit, keeping no margin of its own: as live's Studio
   * filter pills (`search-toolbar/types-filter`), the tabs touching; `"spaced"`, as live's Library
   * pills (`library-assets/.../types-filter`), their 2px gap kept.
   */
  inline?: boolean | "spaced";
}

/** The More tab's value: never a tab's, never chosen. */
const MORE = "\u0000more";
/** The gap between tabs, as `.list` sets it. */
const GAP = 2;

/**
 * How many of the tabs, `widths` wide, fit in `room`: all of them when they do, else as many as
 * fit beside More (`more` wide), and at least the first. `room` 0 is not yet measured: all.
 */
export function tabsThatFit(widths: readonly number[], more: number, room: number): number {
  const across = (count: number) =>
    widths.slice(0, count).reduce((sum, width) => sum + width, 0) + GAP * Math.max(count - 1, 0);
  if (room <= 0 || across(widths.length) <= room) return widths.length;
  let count = widths.length - 1;
  while (count > 1 && across(count) + GAP + more > room) count -= 1;
  return Math.max(count, 1);
}

/**
 * The live app's tab row (design-system.md 3.3, 3.5): borderless tabs with a soft primary pill
 * that slides to the active one. Only the row: the page renders the active tab's content.
 */
export function TypeTabs(props: TypeTabsProps) {
  return props.fold === true ? <FoldingRow {...props} /> : <TabRow {...props} folded={[]} />;
}

function FoldingRow(props: TypeTabsProps) {
  const { tabs } = props;
  const { ref: roomRef, width: room } = useElementSize();
  const { ref: measuredRef, width: measuredWidth } = useElementSize();
  const measure = useRef<HTMLDivElement | null>(null);
  const measureRef = useMergedRef(measure, measuredRef);
  const [widths, setWidths] = useState<{ tabs: number[]; more: number }>({ tabs: [], more: 0 });
  // The tabs' own widths, from a hidden copy of the whole row: again when the tabs change or
  // the copy's width does (the font arriving).
  useLayoutEffect(() => {
    const nodes = [...(measure.current?.querySelectorAll<HTMLElement>("[data-measure]") ?? [])];
    const next = {
      tabs: nodes.slice(0, -1).map((node) => node.offsetWidth),
      more: nodes.at(-1)?.offsetWidth ?? 0,
    };
    setWidths((current) =>
      current.more === next.more && current.tabs.join() === next.tabs.join() ? current : next,
    );
  }, [tabs, measuredWidth]);
  const fit =
    widths.tabs.length === tabs.length
      ? tabsThatFit(widths.tabs, widths.more, room)
      : tabs.length;
  return (
    <div ref={roomRef} className={classes.fold}>
      <div ref={measureRef} className={classes.measure} aria-hidden>
        <Tabs variant="none" value={null}>
          <Tabs.List className={classes.list}>
            {[...tabs, { value: MORE, label: "More", icon: <Ellipsis size={16} /> }].map((tab) => (
              <Tabs.Tab
                key={tab.value}
                value={tab.value}
                tabIndex={-1}
                data-measure
                leftSection={tab.icon}
                rightSection={tab.value === MORE ? <ChevronDown size={14} /> : undefined}
                className={classes.tab}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </div>
      <TabRow {...props} tabs={tabs.slice(0, fit)} folded={tabs.slice(fit)} />
    </div>
  );
}

function TabRow({
  label,
  tabs,
  value,
  onChange,
  inline = false,
  folded,
}: TypeTabsProps & { folded: TypeTab[] }) {
  // The indicator needs the list and the active tab's node. Both are state, so the row renders
  // again once they mount; a tab is kept only when its node is new, so re-rendering keeps nothing.
  const [list, setList] = useState<HTMLDivElement | null>(null);
  const [tabNodes, setTabNodes] = useState<Record<string, HTMLButtonElement>>({});
  const keep = (tab: string) => (node: HTMLButtonElement | null) => {
    if (node === null) return;
    setTabNodes((current) => (current[tab] === node ? current : { ...current, [tab]: node }));
  };
  const all = [...tabs, ...folded];
  // A Coming-soon tab is focusable, so Mantine would choose it on a click or an arrow key; More
  // only opens its menu.
  const choose = (next: string | null) => {
    const tab = all.find((each) => each.value === next);
    if (tab !== undefined && tab.disabled !== true) onChange(tab.value);
  };
  // A folded tab chosen lights More, as live's.
  const shown = folded.some((each) => each.value === value) ? MORE : value;
  return (
    <Tabs variant="none" value={shown} onChange={choose}>
      <Tabs.List ref={setList} aria-label={label} className={classes.list} data-inline={inline || undefined}>
        {tabs.map((tab) => {
          const control = (soon?: SoonProps) => (
            <Tabs.Tab
              {...soon}
              key={tab.value}
              value={tab.value}
              ref={keep(tab.value)}
              leftSection={tab.icon}
              className={classes.tab}
              data-faded={tab.faded === true || undefined}
            >
              {tab.label}
            </Tabs.Tab>
          );
          if (tab.disabled !== true) return control();
          return <Soon key={tab.value}>{control}</Soon>;
        })}
        {folded.length === 0 ? null : (
          <Menu trigger="click-hover" openDelay={100} closeDelay={150} position="bottom-end">
            <Menu.Target>
              <Tabs.Tab
                value={MORE}
                ref={keep(MORE)}
                leftSection={<Ellipsis size={16} />}
                rightSection={<ChevronDown size={14} />}
                className={classes.tab}
              >
                More
              </Tabs.Tab>
            </Menu.Target>
            <Menu.Dropdown>
              {folded.map((tab) => (
                <Menu.Item
                  key={tab.value}
                  leftSection={tab.icon}
                  data-active={tab.value === value || undefined}
                  className={classes.foldedItem}
                  onClick={() => choose(tab.value)}
                >
                  {tab.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
        <FloatingIndicator
          target={tabNodes[shown] ?? null}
          parent={list}
          className={classes.indicator}
        />
      </Tabs.List>
    </Tabs>
  );
}
